<template>
  <div class="map-container">
    <div :id="mapId" class="map-content">
      <!-- 底图切换控件 -->
      <div class="basemap-control">
        <button v-for="type in basemapTypes" :key="type.value" @click="switchBaseMap(type.value)"
          :class="{ active: currentBaseMap === type.value }" style="width: 105px;">
          {{ type.label }}
        </button>
      </div>

      <!-- 图层显示/隐藏控件 -->
      <div class="layer-control">
        <div class="control-title">图层控制</div>
        <div class="layer-toggle" v-for="(config, layerId) in layerConfigs" :key="layerId">
          <a-checkbox v-model="visibleLayers[layerId]" @change="toggleLayerVisibility(layerId)">
            {{ getLayerLabel(layerId) }}
          </a-checkbox>
        </div>
      </div>

      <!-- 绘制控件 -->
      <div class="draw-control">
        <div class="control-title">功能面板</div>
        <div class="tool-buttons">
          <a-tooltip title="绘制分析面" placement="left">
            <div class="tool-btn" :class="{ active: activeToolType === 'analyze-query' }"
              @click="toggleTool('analyze-query')">
              <a-icon type="border-outer" v-if="activeToolType !== 'analyze-query'" />
              <a-icon type="edit" v-else />
              <span>绘制面</span>
            </div>
          </a-tooltip>
          <a-popconfirm title="确认清除所有绘制内容吗？" @confirm="clearQueryPolygon" okText="确认" cancelText="取消" placement="left">
            <a-tooltip title="清除绘制" placement="left">
              <div class="tool-btn">
                <a-icon type="delete" />
                <span>清除</span>
              </div>
            </a-tooltip>
          </a-popconfirm>
        </div>
      </div>
    </div>
    <div class="global-loading" v-if="saving">
      <a-spin size="large" tip="正在处理，请稍候..." />
    </div>

    <!-- 右键菜单 -->
    <div v-if="contextMenu.visible" class="custom-context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }">
      <div class="menu-item" @click="openCoordCorrection">
        <a-icon type="edit" />
        <span>修正坐标</span>
      </div>
      <div class="menu-item" @click="handleDeletePoint">
        <a-icon type="delete" />
        <span>删除顶点</span>
      </div>
      <div class="menu-item" @click="hideContextMenu">
        <a-icon type="close" />
        <span>取消</span>
      </div>
    </div>

    <!-- 坐标修正弹窗 -->
    <a-modal v-model="coordModal.visible" title="修正坐标点" @ok="handleCoordUpdate" @cancel="hideCoordModal" destroyOnClose
      :width="400" :dialogStyle="{ top: '250px' }">
      <a-form-model :model="coordModal.form" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="经度(X)">
          <a-input-number v-model="coordModal.form.lng" :precision="8" :step="0.000001" style="width: 100%" />
        </a-form-model-item>
        <a-form-model-item label="纬度(Y)">
          <a-input-number v-model="coordModal.form.lat" :precision="8" :step="0.000001" style="width: 100%" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import ImageWMS from 'ol/source/ImageWMS';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style, Icon } from 'ol/style';
import * as olProj from 'ol/proj';
import { Point, LineString, Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { Translate } from 'ol/interaction';
import { getUUID } from '@/utils/util';
import storage from 'store';
import { LonLat } from '@/store/mutation-types';
import { Checkbox, Button, Spin, Tooltip, Modal, InputNumber, FormModel, Popconfirm } from 'ant-design-vue';

export default {
  name: 'WaterPlantMap',
  components: {
    'a-checkbox': Checkbox,
    'a-button': Button,
    'a-spin': Spin,
    'a-tooltip': Tooltip,
    'a-modal': Modal,
    'a-input-number': InputNumber,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-popconfirm': Popconfirm
  },
  props: {
    mode: {
      type: String,
      default: 'view'
    },
    value: {
      type: String,
      default: ''
    }
  },
  watch: {
    value (newVal) {
      // 只有当新值与当前图面生成的 GeoJSON 不同时，才重新加载
      // 这样可以支持在文本框手动修改后，地图同步更新
      const currentGeoJson = this.getCurrentGeoJsonStr();
      if (newVal !== currentGeoJson) {
        this.loadInitialValue();
      }
    }
  },
  data () {
    return {
      mapId: getUUID(),
      map: null,
      gcjMercator: null,
      currentBaseMap: 'gaode',
      visibleLayers: {},
      vectorSource: null,
      vectorLayer: null,
      wmsLayers: {},
      baseLayer: null,
      saving: false,
      activeToolType: '', // 当前激活的工具类型
      queryPolygonPoints: [], // 存储多边形点的 Feature 对象
      queryPolygonMidPoints: [], // 存储中点的 Feature 对象
      queryPolygonSource: null,
      queryPolygonLayer: null,
      queryPolygon: null,
      queryPolygonTranslate: null, // 拖拽交互
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        feature: null
      },
      coordModal: {
        visible: false,
        form: {
          lng: 0,
          lat: 0
        },
        feature: null
      },
      basemapTypes: [
        { value: 'gaode', label: '高德地图' },
        { value: 'tianditu_img', label: '天地影像' },
      ],
      baseMapUrls: {
        gaode: 'http://wprd0{1-4}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}',
        tianditu_img: 'https://t7.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=bbb8e87c166f332a270fa223ba022abb'
      },
      layerConfigs: {
        water_factory_area: {
          workspace: 'smart_water',
          dataSource: 'smart_water:water_factory_area',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}'`,
          label: '水厂范围'
        },
        gis_pipe_line: {
          workspace: 'smart_water',
          dataSource: 'smart_water:gis_pipe_line',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}'`,
          label: '管线图层'
        },
        zone_associated_point_valve: {
          workspace: 'smart_water',
          dataSource: 'smart_water:zone_associated_point',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}' and appendage = '分界阀门'`,
          label: '分界阀门'
        },
        zone_associated_point_meter: {
          workspace: 'smart_water',
          dataSource: 'smart_water:remote_water_meter',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}'`,
          label: '考核流量计'
        }
      }
    };
  },
  mounted () {
    this.initVisibleLayers();
    this.initMap();
  },
  beforeDestroy () {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = null;
    }
  },
  methods: {
    initVisibleLayers () {
      Object.keys(this.layerConfigs).forEach(layerId => {
        this.$set(this.visibleLayers, layerId, true);
      });
    },
    getLayerLabel (layerId) {
      return this.layerConfigs[layerId]?.label || layerId;
    },
    toggleLayerVisibility (layerId) {
      const layer = this.wmsLayers[layerId];
      if (layer) {
        layer.setVisible(this.visibleLayers[layerId]);
      } else if (this.visibleLayers[layerId]) {
        this.loadGeoserverLayer(layerId);
      }
    },
    initMap () {
      this.setupGcjProjection();
      const center = storage.get(LonLat) || [106.456878, 29.541145];

      this.vectorSource = new VectorSource();
      this.vectorLayer = new VectorLayer({
        source: this.vectorSource,
        style: new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: 'red' }),
            stroke: new Stroke({ color: 'black', width: 2 })
          })
        })
      });

      this.map = new Map({
        view: new View({
          center: center,
          projection: 'EPSG:4326',
          zoom: 12,
          maxZoom: 28,
          minZoom: 1
        }),
        target: this.mapId,
        layers: []
      });

      this.switchBaseMap(this.currentBaseMap);
      this.map.addLayer(this.vectorLayer);
      this.loadAllGeoserverLayers();
      this.initQueryPolygonLayer();

      // 如果有初始值，进行加载
      if (this.value) {
        this.loadInitialValue();
      }

      this.map.on('click', (event) => {
        if (this.activeToolType === 'analyze-query') {
          // 检查是否点击了中点
          const features = this.map.getFeaturesAtPixel(event.pixel);
          if (features.length > 0) {
            const feature = features[0];
            // 处理查询多边形中点点击
            if (feature.get('type') === 'query-polygon-midpoint') {
              this.handleQueryPolygonMidPointClick(feature);
              return;
            }
          }
          this.addQueryPolygonPoint(event.coordinate);
        }
      });

      // 禁用默认右键菜单并处理自定义右键逻辑
      const viewport = this.map.getViewport();
      viewport.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        // 编辑模式下或者工具激活时允许右键
        if (this.activeToolType === 'analyze-query' || this.mode === 'edit') {
          const pixel = this.map.getEventPixel(e);
          const features = this.map.getFeaturesAtPixel(pixel);
          if (features.length > 0) {
            const feature = features.find(f => f.get('type') === 'query-polygon-point');
            if (feature) {
              this.showContextMenu(e, feature);
            }
          }
        }
      });

      // 点击地图其他地方隐藏右键菜单
      this.map.on('pointerdown', () => {
        if (this.contextMenu.visible) {
          this.hideContextMenu();
        }
      });

      requestAnimationFrame(() => {
        this.map.renderSync();
        this.map.updateSize();
      });
    },
    toggleTool (toolType) {
      if (this.activeToolType === toolType) {
        this.activeToolType = '';
      } else {
        this.activeToolType = toolType;
        if (toolType === 'analyze-query') {
          if (!this.queryPolygonLayer) {
            this.initQueryPolygonLayer();
          }
        }
      }
    },
    // 初始化查询多边形图层
    initQueryPolygonLayer () {
      if (this.queryPolygonLayer) return;

      this.queryPolygonSource = new VectorSource();
      this.queryPolygonLayer = new VectorLayer({
        source: this.queryPolygonSource,
        zIndex: 1004,
        properties: {
          layerType: 'query-polygon'
        },
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'query-polygon-point') {
            return new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({
                  color: '#1890ff'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'query-polygon-line') {
            return new Style({
              stroke: new Stroke({
                color: '#1890ff',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'query-polygon') {
            return new Style({
              fill: new Fill({
                color: 'rgba(24, 144, 255, 0.2)'
              }),
              stroke: new Stroke({
                color: '#1890ff',
                width: 2
              })
            });
          } else if (type === 'query-polygon-midpoint') {
            return new Style({
              image: new CircleStyle({
                radius: 5,
                fill: new Fill({
                  color: 'rgba(24, 144, 255, 0.5)'
                }),
                stroke: new Stroke({
                  color: 'rgba(24, 144, 255, 0.8)',
                  width: 1
                })
              })
            });
          }
        }
      });
      this.map.addLayer(this.queryPolygonLayer);

      // 添加拖拽交互
      this.queryPolygonTranslate = new Translate({
        features: this.queryPolygonSource.getFeaturesCollection(),
        filter: (feature) => {
          return feature.get('type') === 'query-polygon-point';
        }
      });
      this.map.addInteraction(this.queryPolygonTranslate);
      this.queryPolygonTranslate.setActive(false);

      // 监听拖拽变化
      this.queryPolygonSource.on('changefeature', this.updateQueryPolygonGeometries);
    },
    // 添加查询多边形点
    addQueryPolygonPoint (coordinate) {
      // 创建查询多边形点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'query-polygon-point',
        index: this.queryPolygonPoints.length
      });

      this.queryPolygonPoints.push(pointFeature);
      this.queryPolygonSource.addFeature(pointFeature);

      if (this.queryPolygonPoints.length > 1) {
        const lineCoordinates = this.queryPolygonPoints.slice(-2).map(p => p.getGeometry().getCoordinates());
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: this.queryPolygonPoints.length - 2
        });

        this.queryPolygonSource.addFeature(lineFeature);

        if (this.queryPolygonPoints.length >= 3) {
          this.updateQueryPolygon();
        }
      }
    },
    // 更新查询多边形
    updateQueryPolygon () {
      if (this.queryPolygon) {
        this.queryPolygonSource.removeFeature(this.queryPolygon);
      }

      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      if (pointCoords.length > 2) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        const polygon = new Polygon([polygonCoordinates]);
        const polygonFeature = new Feature({
          geometry: polygon,
          type: 'query-polygon'
        });

        this.queryPolygonSource.addFeature(polygonFeature);
        this.queryPolygon = polygonFeature;

        // 添加中点并启用拖拽
        this.addQueryPolygonMidPoints();
        if (this.queryPolygonTranslate) {
          this.queryPolygonTranslate.setActive(true);
        }
      }
      // 实时同步数据
      this.syncGeoJson();
    },
    syncGeoJson () {
      const geoJson = this.getCurrentGeoJsonStr();
      this.$emit('change', geoJson);
    },
    getCurrentGeoJsonStr () {
      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());
      if (pointCoords.length >= 3) {
        const geoJsonCoords = [...pointCoords, pointCoords[0]];
        return JSON.stringify({
          type: 'Polygon',
          coordinates: [geoJsonCoords]
        });
      }
      return '';
    },
    // 处理查询多边形中点点击
    handleQueryPolygonMidPointClick (feature) {
      const edgeIndex = feature.get('edgeIndex');
      const midPointCoords = feature.getGeometry().getCoordinates();

      // 创建新的点要素
      const newPoint = new Point(midPointCoords);
      const newPointFeature = new Feature({
        geometry: newPoint,
        type: 'query-polygon-point',
        index: this.queryPolygonPoints.length
      });

      // 在正确的位置插入新点
      const insertIndex = (edgeIndex + 1) % this.queryPolygonPoints.length;
      this.queryPolygonPoints.splice(insertIndex, 0, newPointFeature);
      this.queryPolygonSource.addFeature(newPointFeature);

      // 更新所有点的索引
      this.queryPolygonPoints.forEach((point, index) => {
        point.set('index', index);
      });

      // 更新几何图形
      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 更新线段
      const features = this.queryPolygonSource.getFeatures();
      features.forEach(f => {
        if (f.get('type') === 'query-polygon-line') {
          this.queryPolygonSource.removeFeature(f);
        }
      });

      for (let i = 0; i < this.queryPolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: i
        });
        this.queryPolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.queryPolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.queryPolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addQueryPolygonMidPoints();

      // 实时同步数据
      this.syncGeoJson();
    },
    // 更新查询多边形几何图形（拖拽后）
    updateQueryPolygonGeometries (event) {
      const movedFeature = event.feature;
      if (!movedFeature || movedFeature.get('type') !== 'query-polygon-point') return;

      // 获取所有点的坐标
      const pointCoords = this.queryPolygonPoints.map(point => point.getGeometry().getCoordinates());

      // 更新线段
      const features = this.queryPolygonSource.getFeatures();
      features.forEach(feature => {
        if (feature.get('type') === 'query-polygon-line') {
          this.queryPolygonSource.removeFeature(feature);
        }
      });

      // 重新创建线段
      for (let i = 0; i < this.queryPolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: i
        });
        this.queryPolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.queryPolygon) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        this.queryPolygon.getGeometry().setCoordinates([polygonCoordinates]);
      }

      // 更新中点
      this.addQueryPolygonMidPoints();

      // 实时同步数据
      this.syncGeoJson();
    },
    // 添加查询多边形的中点
    addQueryPolygonMidPoints () {
      // 清除旧的中点
      this.clearQueryPolygonMidPoints();

      if (this.queryPolygonPoints.length >= 2) {
        for (let i = 0; i < this.queryPolygonPoints.length; i++) {
          const start = this.queryPolygonPoints[i].getGeometry().getCoordinates();
          const end = this.queryPolygonPoints[(i + 1) % this.queryPolygonPoints.length].getGeometry().getCoordinates();
          const midPoint = this.calculateQueryMidPoint(start, end);

          const pointFeature = new Feature({
            geometry: new Point(midPoint),
            type: 'query-polygon-midpoint',
            edgeIndex: i
          });

          this.queryPolygonMidPoints.push(pointFeature);
          this.queryPolygonSource.addFeature(pointFeature);
        }
      }
    },
    // 清除查询多边形中点
    clearQueryPolygonMidPoints () {
      this.queryPolygonMidPoints.forEach(feature => {
        this.queryPolygonSource.removeFeature(feature);
      });
      this.queryPolygonMidPoints = [];
    },
    // 计算线段中点
    calculateQueryMidPoint (start, end) {
      return [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2
      ];
    },
    calculatePolygonCenter (coordinates) {
      let x = 0;
      let y = 0;
      coordinates.forEach(coord => {
        x += coord[0];
        y += coord[1];
      });
      return [x / coordinates.length, y / coordinates.length];
    },
    loadInitialValue () {
      if (!this.map || !this.queryPolygonSource) return;
      try {
        if (!this.value) {
          this.clearQueryPolygon();
          return;
        }
        const geoData = JSON.parse(this.value);
        if (geoData.type === 'Polygon' && geoData.coordinates && geoData.coordinates[0]) {
          // 如果是编辑模式，自动激活工具
          if (this.mode === 'edit') {
            this.activeToolType = 'analyze-query';
          }

          // 先清除当前正在绘制的内容
          this.queryPolygonPoints = [];
          if (this.queryPolygonSource) {
            this.queryPolygonSource.clear();
          }
          this.queryPolygon = null;
          this.clearQueryPolygonMidPoints();

          // GeoJSON 的最后一个点通常与第一个点重复，ol 绘制点时不需要重复点
          const coords = geoData.coordinates[0];
          // 如果最后一个点等于第一个点，去掉最后一个
          if (coords.length > 1 &&
            coords[0][0] === coords[coords.length - 1][0] &&
            coords[0][1] === coords[coords.length - 1][1]) {
            coords.pop();
          }

          coords.forEach(coord => {
            this.addQueryPolygonPoint(coord);
          });

          // 自动缩放到多边形区域
          if (this.queryPolygon) {
            this.map.getView().fit(this.queryPolygon.getGeometry().getExtent(), {
              padding: [50, 50, 50, 50],
              duration: 500
            });
          }
        }
      } catch (e) {
        console.error('解析初始 GeoJSON 失败:', e);
      }
    },
    showContextMenu (e, feature) {
      this.contextMenu.visible = true;
      this.contextMenu.x = e.clientX;
      this.contextMenu.y = e.clientY;
      this.contextMenu.feature = feature;
    },
    hideContextMenu () {
      this.contextMenu.visible = false;
      this.contextMenu.feature = null;
    },
    openCoordCorrection () {
      if (!this.contextMenu.feature) return;
      const feature = this.contextMenu.feature;
      const coord = feature.getGeometry().getCoordinates();

      this.coordModal.feature = feature;
      this.coordModal.form.lng = coord[0];
      this.coordModal.form.lat = coord[1];
      this.coordModal.visible = true;

      this.hideContextMenu();
    },
    hideCoordModal () {
      this.coordModal.visible = false;
      this.coordModal.feature = null;
    },
    handleCoordUpdate () {
      const { lng, lat } = this.coordModal.form;
      if (!lng || !lat) {
        this.$message.warning('请输入有效的坐标');
        return;
      }

      const feature = this.coordModal.feature;
      if (feature) {
        // 更新几何位置
        feature.getGeometry().setCoordinates([lng, lat]);
        // 重新绘制多边形和线段
        this.refreshPolygonGeometries();
        this.$message.success('坐标修正成功');
      }

      this.hideCoordModal();
    },
    handleDeletePoint () {
      if (!this.contextMenu.feature) return;
      const feature = this.contextMenu.feature;
      const index = feature.get('index');

      // 从数组中删除
      this.queryPolygonPoints.splice(index, 1);
      // 从数据源中删除
      this.queryPolygonSource.removeFeature(feature);

      // 更新剩余点的索引
      this.queryPolygonPoints.forEach((p, i) => {
        p.set('index', i);
      });

      // 重新绘制线和面
      this.refreshPolygonGeometries();

      this.hideContextMenu();
      this.$message.success('顶点已删除');
    },
    refreshPolygonGeometries () {
      const pointCoords = this.queryPolygonPoints.map(p => p.getGeometry().getCoordinates());

      // 清除旧的线
      const features = this.queryPolygonSource.getFeatures();
      features.forEach(f => {
        if (f.get('type') === 'query-polygon-line') {
          this.queryPolygonSource.removeFeature(f);
        }
      });

      // 如果点数不足，清除多边形
      if (this.queryPolygonPoints.length < 3) {
        if (this.queryPolygon) {
          this.queryPolygonSource.removeFeature(this.queryPolygon);
          this.queryPolygon = null;
        }
      }

      // 重新创建线
      for (let i = 0; i < this.queryPolygonPoints.length - 1; i++) {
        const lineString = new LineString([pointCoords[i], pointCoords[i + 1]]);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'query-polygon-line',
          index: i
        });
        this.queryPolygonSource.addFeature(lineFeature);
      }

      // 更新多边形
      if (this.queryPolygonPoints.length >= 3) {
        const polygonCoordinates = [...pointCoords, pointCoords[0]];
        if (this.queryPolygon) {
          this.queryPolygon.getGeometry().setCoordinates([polygonCoordinates]);
        } else {
          const polygon = new Polygon([polygonCoordinates]);
          const polygonFeature = new Feature({
            geometry: polygon,
            type: 'query-polygon'
          });
          this.queryPolygonSource.addFeature(polygonFeature);
          this.queryPolygon = polygonFeature;
        }
      }

      // 更新中点
      this.addQueryPolygonMidPoints();

      // 实时同步数据
      this.syncGeoJson();
    },
    clearQueryPolygon () {
      this.queryPolygonPoints = [];
      this.queryPolygon = null;

      // 清除中点
      this.clearQueryPolygonMidPoints();

      // 禁用拖拽
      if (this.queryPolygonTranslate) {
        this.queryPolygonTranslate.setActive(false);
      }

      if (this.queryPolygonSource) {
        this.queryPolygonSource.clear();
      }
      this.activeToolType = '';
      this.syncGeoJson();
    },
    setupGcjProjection () {
      const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
      this.gcjMercator = new olProj.Projection({
        code: 'GCJ-02',
        extent: gcj02Extent,
        units: 'm'
      });
      olProj.addProjection(this.gcjMercator);
      if (window.projzh) {
        olProj.addCoordinateTransforms('EPSG:4326', this.gcjMercator, window.projzh.ll2gmerc, window.projzh.gmerc2ll);
        olProj.addCoordinateTransforms('EPSG:3857', this.gcjMercator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc);
      }
    },
    switchBaseMap (type) {
      if (this.baseLayer) {
        this.map.removeLayer(this.baseLayer);
      }
      this.currentBaseMap = type;
      const url = typeof this.baseMapUrls[type] === 'function' ? this.baseMapUrls[type]() : this.baseMapUrls[type];
      const isGcj = type === 'gaode';

      this.baseLayer = new TileLayer({
        source: new XYZ({
          projection: isGcj ? this.gcjMercator : 'EPSG:3857',
          crossOrigin: 'anonymous',
          url: url,
          maxZoom: 18,
          tileSize: 256
        })
      });
      this.map.getLayers().insertAt(0, this.baseLayer);
    },
    loadAllGeoserverLayers () {
      Object.keys(this.layerConfigs).forEach(id => this.loadGeoserverLayer(id));
    },
    loadGeoserverLayer (layerId) {
      const config = this.layerConfigs[layerId];
      if (!config) return;

      if (this.wmsLayers[layerId]) {
        this.map.removeLayer(this.wmsLayers[layerId]);
      }

      const wmsSource = new ImageWMS({
        url: `/api/geoserver/${config.workspace}/wms?_t=${Date.now()}`,
        params: {
          'LAYERS': config.dataSource,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.0',
          'CQL_FILTER': config.showFilterCondition
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous'
      });

      const wmsLayer = new ImageLayer({
        source: wmsSource,
        properties: { id: layerId },
        visible: this.visibleLayers[layerId] !== false,
        zIndex: 1
      });

      this.wmsLayers[layerId] = wmsLayer;
      this.map.addLayer(wmsLayer);
    }
  }
};
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;

  .map-content {
    width: 100%;
    height: 100%;
  }
}

.basemap-control {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);

  button {
    display: block;
    width: 100%;
    margin: 2px 0;
    padding: 5px 10px;
    border: none;
    background: #f8f8f8;
    cursor: pointer;
    border-radius: 3px;
    font-size: 12px;

    &:hover {
      background: #e0e0e0;
    }

    &.active {
      background: #3388ff;
      color: white;
    }
  }
}

.layer-control {
  position: absolute;
  top: 130px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);

  .control-title {
    font-weight: bold;
    margin-bottom: 8px;
    font-size: 13px;
    text-align: center;
  }

  .layer-toggle {
    margin: 5px 0;
    white-space: nowrap;
  }
}

.draw-control {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);

  .control-title {
    font-weight: bold;
    margin-bottom: 8px;
    font-size: 13px;
    text-align: center;
  }

  .tool-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .tool-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 60px;
      height: 60px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
      background: #fff;

      i {
        font-size: 20px;
        margin-bottom: 4px;
      }

      span {
        font-size: 12px;
      }

      &:hover {
        color: #1890ff;
        border-color: #1890ff;
      }

      &.active {
        background: #1890ff;
        color: #fff;
        border-color: #1890ff;
      }

      &.success {
        color: #52c41a;
        border-color: #52c41a;

        &:hover {
          background: #f6ffed;
        }
      }
    }
  }
}

.global-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.custom-context-menu {
  position: fixed;
  z-index: 10000;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 100px;

  .menu-item {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    transition: all 0.3s;

    &:hover {
      background-color: #f5f5f5;
      color: #1890ff;
    }

    i {
      font-size: 14px;
    }
  }
}
</style>
